import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { whenText } from "../lib/kidLook";
import { MessageCircle, Send, DollarSign, Bell, History, LogOut } from "lucide-react";

// The kid's page on yoda.watch, made to look like the kid app (KidTheme in ios/YodaWatch/Views/KidTheme.swift).
const K = { bg: "#F7F0DE", ink: "#29241A", moss: "#5C7A4F", sky: "#4D9ED9", sun: "#F2B544", coral: "#E66B59" };
const REASON = {
  paused: "Mum or Dad paused screens", schedule: "It's bedtime", outside_window: "Outside your allowed hours",
  quota_exhausted: "You're out of time for today", tasks_pending: "Finish your jobs first", agreement_unsigned: "Sign the family agreement first",
  agreement_broken: "The agreement was broken", event: "Time to get ready",
  app_deleted: "Yoda Watch was deleted from your phone. Put it back to unlock",
  bedtime: "It's bedtime 🌙 See you in the morning",
};

function hm(sec) {
  const s = Math.max(0, Math.floor(sec || 0)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtTime(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}`;
}

function Ring({ left, total, colour, label = "left today", track = "#EFE7D3" }) {
  const r = 56, len = 2 * Math.PI * r, frac = total > 0 ? Math.max(0, Math.min(1, left / total)) : 0;
  return (
    <div className="relative h-[132px] w-[132px] flex-none">
      <svg viewBox="0 0 132 132" width="132" height="132">
        <circle cx="66" cy="66" r={r} fill="none" stroke={track} strokeWidth="15" />
        <circle cx="66" cy="66" r={r} fill="none" stroke={colour} strokeWidth="15" strokeLinecap="round"
                strokeDasharray={`${frac * len} ${len}`} transform="rotate(-90 66 66)" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[24px] font-extrabold tracking-tight" style={{ color: K.ink }}>{hm(left)}</div>
        <div className="text-[13px] font-semibold" style={{ color: "#6F6656" }}>{label}</div>
      </div>
    </div>
  );
}


export default function KidHome() {
  const [me, setMe] = useState(null);
  const [rules, setRules] = useState([]);
  const [today, setToday] = useState(null);
  const [notices, setNotices] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [cashouts, setCashouts] = useState([]);
  const [reductions, setReductions] = useState([]);
  const [tab, setTab] = useState("Jobs");
  const [expanded, setExpanded] = useState(null);
  const [questions, setQuestions] = useState({});
  const [drafts, setDrafts] = useState({});
  const [cashoutOpen, setCashoutOpen] = useState(false);
  const [cashoutForm, setCashoutForm] = useState({ amount: "", note: "" });
  const [cashoutErr, setCashoutErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/kid-me").then((r) => setMe(r.data)).catch(() => setMe(false));
  }, []);

  const loadAll = () => {
    api.get("/kid/rules").then((r) => setRules(r.data));
    api.get("/kid/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/kid/notifications").then((r) => setNotices(r.data || [])).catch(() => {});
    api.get("/kid/wallet").then((r) => setWallet(r.data));
    api.get("/kid/payouts").then((r) => setPayouts(r.data));
    api.get("/kid/cashouts").then((r) => setCashouts(r.data));
    api.get("/kid/reductions").then((r) => setReductions(r.data));
  };
  useEffect(() => { if (me) { loadAll(); const t = setInterval(loadAll, 15000); return () => clearInterval(t); } }, [me]);

  if (me === null) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!me) return <Navigate to="/kid/login" replace />;

  const loadQuestions = async (ruleId) => {
    const r = await api.get(`/kid/rules/${ruleId}/questions`);
    setQuestions((q) => ({ ...q, [ruleId]: r.data }));
  };
  const toggleRule = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!questions[id]) await loadQuestions(id);
  };
  const askQuestion = async (ruleId) => {
    const q = drafts[ruleId]; if (!q) return;
    await api.post(`/kid/rules/${ruleId}/question`, { question: q });
    setDrafts((d) => ({ ...d, [ruleId]: "" }));
    loadQuestions(ruleId);
  };
  const submitCashout = async (e) => {
    e.preventDefault();
    setCashoutErr("");
    try {
      await api.post("/kid/cashout", { amount: parseFloat(cashoutForm.amount), note: cashoutForm.note || null });
      setCashoutOpen(false);
      setCashoutForm({ amount: "", note: "" });
      loadAll();
    } catch (e) {
      setCashoutErr(e.response?.data?.detail || "Try again");
    }
  };
  const logout = async () => { await api.post("/kid-auth/logout"); navigate("/kid/login"); };

  const pendingCashout = cashouts.find((c) => c.status === "pending");
  const latestReduction = reductions[0];

  return (
    <div className="min-h-screen kid-font" style={{ background: K.bg, color: K.ink }}>
      <main className="mx-auto max-w-[700px] space-y-4 px-4 py-6" data-testid="kid-home">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[15px] font-semibold text-[#6F6656]">
              {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <h1 className="text-[32px] font-extrabold tracking-tight">Hi {me.name} 👋</h1>
          </div>
          <div className="flex gap-2">
          <button onClick={() => setTab(tab === "Notifications" ? "Jobs" : "Notifications")} data-testid="kid-notices-btn"
                  title="Notifications"
                  className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold shadow-sm ${tab === "Notifications" ? "bg-[#29241A] text-white" : "bg-white text-[#6F6656]"}`}>
            <Bell size={15} /> <span className="hidden sm:inline">Notifications</span>
          </button>
          <button onClick={logout} data-testid="kid-logout-btn" title="Sign out"
                  className="flex items-center gap-1.5 rounded-full bg-white px-3 py-2 text-sm font-bold text-[#6F6656] shadow-sm">
            <LogOut size={15} /> <span className="hidden sm:inline">Sign out</span>
          </button>
          </div>
        </div>

        {today && (() => {
          const p = today.policy || {};
          const left = p.remaining_today_seconds || 0, total = p.total_today_seconds || 0;
          const why = today.paused ? "paused" : (p.shielded ? p.reason : null);
          const low = !why && left < 600;
          const colour = why ? K.coral : low ? K.sun : K.sky;
          const unlimited = !why && p.unlimited_active;
          const pill = why ? (REASON[why] || "Screens are off")
                     : unlimited ? `Unlimited until ${whenText(p.unlimited_until)}`
                     : p.override_active ? `Extra time · ${hm(p.override_remaining_seconds)}` : low ? "Nearly out of time" : "You're good to go";
          // The ring always shows their own hours; extra time is on top, shown underneath (Oscar, 5 Nov 2026).
          const extra = !why && p.override_active;
          const extraLeft = p.override_remaining_seconds || 0;
          const extraGiven = Math.max(p.override_given_seconds || 0, extraLeft);
          const pillBg = why ? "#FBE3DE" : unlimited ? "#EEE8FA" : extra ? "#E1EFF9" : low ? "#FDF1D8" : "#E6EDE1";
          const pillFg = why ? "#B8483A" : unlimited ? "#6A4BB0" : extra ? "#2D79B0" : low ? "#A87414" : K.moss;
          return (
            <div className="flex items-center gap-4 rounded-[26px] bg-white p-4 shadow-[0_3px_12px_rgba(41,36,26,0.06)]" data-testid="kid-time-card">
              <Ring left={left} total={total} colour={colour} />
              <div className="min-w-0 space-y-2">
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[14px] font-bold"
                      style={{ background: pillBg, color: pillFg }}>
                  <i className="h-[7px] w-[7px] rounded-full" style={{ background: pillFg }} /> {pill}
                </span>
                <div className="text-[15px] font-semibold text-[#6F6656]">
                  Played <b style={{ color: K.ink }}>{hm(p.used_today_seconds)}</b> of <b style={{ color: K.ink }}>{hm(total)}</b>
                </div>
                {extra && (
                  <div className="text-[15px] font-semibold text-[#6F6656]" data-testid="kid-extra-given">
                    Extra time: <b style={{ color: "#2D79B0" }}>{hm(extraLeft)}</b> left of <b style={{ color: "#2D79B0" }}>{hm(extraGiven)}</b>
                  </div>
                )}
                {p.extra_used_today_seconds > 0 && (
                  <div className="text-[15px] font-semibold text-[#6F6656]" data-testid="kid-extra-played">
                    Extra played today: <b style={{ color: "#2D79B0" }}>{hm(p.extra_used_today_seconds)}</b>
                  </div>
                )}
                {p.window?.end && (
                  <div className="text-[15px] font-semibold text-[#6F6656]">
                    Screens off at <b style={{ color: K.ink }}>{fmtTime(p.window.end)}</b>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {latestReduction && (
          <div className="flex items-start gap-3 rounded-[22px] bg-white p-4" data-testid="kid-reduction-banner">
            <Bell size={18} className="mt-0.5 text-[#E66B59]" />
            <div className="flex-1">
              <div className="text-sm font-bold">{latestReduction.message || `-${latestReduction.minutes} min`}</div>
              <div className="mt-0.5 text-xs text-[#6F6656]">{new Date(latestReduction.applied_at).toLocaleString()}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {(() => {
            const day = (today?.jobs?.jobs || []).filter((j) => j.date === today.jobs.date);
            const done = day.filter((j) => j.done).length;
            return (
              <button onClick={() => setTab("Jobs")} className="rounded-[24px] bg-white p-4 text-left shadow-[0_3px_12px_rgba(41,36,26,0.06)]"
                      data-testid="kid-jobs-tile">
                <span className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-[#E6EDE1] text-xl">✅</span>
                <div className="mt-2 text-[18px] font-extrabold">Jobs</div>
                <div className="text-[14px] font-semibold text-[#6F6656]">
                  {day.length ? `${done} of ${day.length} done` : "Nothing today"}
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#EFE7D3]">
                  <i className="block h-full rounded-full" style={{ width: `${day.length ? (done / day.length) * 100 : 0}%`, background: K.moss }} />
                </div>
              </button>
            );
          })()}
          {wallet && (
            <div className="rounded-[24px] bg-white p-4 shadow-[0_3px_12px_rgba(41,36,26,0.06)]" data-testid="kid-wallet-card">
              <span className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-[#FDF1D8] text-xl">💰</span>
              <div className="mt-2 text-[18px] font-extrabold" data-testid="kid-balance">${wallet.balance.toFixed(2)}</div>
              <button onClick={() => setCashoutOpen(true)} disabled={wallet.balance <= 0 || !!pendingCashout} data-testid="kid-cashout-btn"
                      className="text-left text-[14px] font-bold text-[#5C7A4F] disabled:font-semibold disabled:text-[#6F6656]">
                {pendingCashout ? `Waiting on Mum or Dad for $${pendingCashout.amount.toFixed(2)}`
                  : wallet.balance > 0 ? `Cash out $${wallet.balance.toFixed(2)} ›` : "Earn tokens to cash out"}
              </button>
            </div>
          )}
        </div>

        <div className="flex rounded-[15px] bg-[#EFE7D3] p-[3px]">
          {["Jobs", "Rules", "Tokens", "History"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`kid-tab-${t.toLowerCase()}`}
                    className={`flex-1 rounded-[12px] py-2 text-[14.5px] font-bold ${tab === t ? "bg-white shadow-sm" : "text-[#6F6656]"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Notifications" && (
          <div className="space-y-2" data-testid="kid-notices">
            <div className="text-xs font-extrabold uppercase tracking-[0.1em] text-[#8A806C]">Notifications · last 30 days</div>
            {notices.length === 0 && <p className="text-sm text-[#6F6656]">No notifications yet. 🌿</p>}
            {notices.map((n) => (
              <div key={n.id} className="rounded-[20px] bg-white p-3.5" data-testid={`kid-notice-${n.id}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-[16px] font-bold">{n.title || "Yoda Watch"}</div>
                  <div className="shrink-0 text-xs text-[#8A806C]">
                    {n.at ? new Date(n.at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" }) : ""}
                  </div>
                </div>
                {n.body && <div className="mt-0.5 text-[14.5px] text-[#6F6656]">{n.body}</div>}
              </div>
            ))}
          </div>
        )}

        {tab === "Jobs" && (
          <div className="space-y-2" data-testid="kid-jobs">
            {(today?.jobs?.jobs || []).length === 0 && <p className="text-sm text-[#6F6656]">No jobs right now. 🌿</p>}
            {(today?.jobs?.jobs || []).map((j, i, all) => {
              const first = i === 0 || all[i - 1].date !== j.date;
              const isToday = j.date === today.jobs.date;
              const due = j.due_at ? new Date(j.due_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
              const label = isToday ? "Today" : new Date(j.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long" });
              return (
                <div key={j.id}>
                  {first && <div className="mb-2 mt-3 text-xs font-extrabold uppercase tracking-[0.1em] text-[#8A806C]">{label}</div>}
                  <div className={`flex items-center gap-3 rounded-[20px] bg-white p-3 ${isToday ? "" : "opacity-85"}`} data-testid={`kid-job-${j.id}`}>
                    <span className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#F7F0DE] text-2xl">{j.emoji || "✅"}</span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[16px] font-bold ${j.done ? "text-[#8A806C] line-through" : ""}`}>{j.title}</div>
                      <div className="text-[13px] font-semibold text-[#6F6656]">
                        {[due && `by ${due}`, j.photo_required && "photo", j.reward, j.unlocks && "unlocks screens"].filter(Boolean).join(" · ") || " "}
                      </div>
                    </div>
                    <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-white ${j.done ? "border-[#5C7A4F] bg-[#5C7A4F]" : "border-[#D9CFB8]"}`}>
                      {j.done ? "✓" : ""}
                    </span>
                  </div>
                </div>
              );
            })}
            <p className="pt-1 text-xs text-[#8A806C]">Tick jobs off in the Yoda Watch app on your iPad or iPhone.</p>
          </div>
        )}

        {tab === "Rules" && (
          <div className="space-y-3">
            {rules.map((r) => (
              <div key={r.id} className="rounded-[20px] bg-white p-4" data-testid={`kid-rule-${r.id}`}>
                <button onClick={() => toggleRule(r.id)} className="w-full text-left">
                  <div className="font-medium text-[#29241A]">{r.title}</div>
                </button>
                {expanded === r.id && (
                  <div className="mt-3 space-y-3">
                    {r.description && <p className="text-sm text-[#6F6656] whitespace-pre-wrap">{r.description}</p>}
                    <div className="border-t border-[#E8DFC9] pt-3 space-y-2">
                      <div className="flex items-center gap-1 text-xs text-[#6F6656]"><MessageCircle size={12} /> Ask a question</div>
                      {(questions[r.id] || []).map((q) => (
                        <div key={q.id} className="text-sm bg-[#F7F0DE] rounded-xl p-3">
                          <div className="text-[#29241A]"><b>You:</b> {q.question}</div>
                          {q.answer ? (
                            <div className="text-[#5C7A4F] mt-1"><b>Mum/Dad:</b> {q.answer}</div>
                          ) : (
                            <div className="text-xs text-[#6F6656] mt-1 italic">waiting for a reply…</div>
                          )}
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input placeholder="Type a question…" value={drafts[r.id] || ""}
                               onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })}
                               className="flex-1 rounded-xl border border-[#E8DFC9] bg-white px-3 py-2 text-sm"
                               data-testid={`kid-question-input-${r.id}`} />
                        <button onClick={() => askQuestion(r.id)} className="rounded-xl bg-[#5C7A4F] text-white px-3 hover:bg-[#4B6640]"
                                data-testid={`kid-question-send-${r.id}`}>
                          <Send size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {rules.length === 0 && <p className="text-[#6F6656] text-sm">No family rules yet.</p>}
          </div>
        )}

        {tab === "Tokens" && wallet && (
          <div className="space-y-2">
            {wallet.tokens.length === 0 && <p className="text-[#6F6656] text-sm">No tokens yet.</p>}
            {wallet.tokens.map((t) => (
              <div key={t.token_type_id} className="rounded-[20px] bg-white p-4 flex items-center justify-between" data-testid={`kid-token-${t.token_type_id}`}>
                <div>
                  <div className="font-medium text-[#29241A]">{t.name}</div>
                  {t.category && <div className="text-xs text-[#6F6656]">{t.category}</div>}
                </div>
                <div className="text-right">
                  <div className="text-lg font-heading font-semibold text-[#5C7A4F]">×{t.count}</div>
                  <div className="text-xs text-[#6F6656]">${t.earned_dollars.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "History" && (
          <div className="space-y-4">
            <section data-testid="kid-payout-history">
              <div className="flex items-center gap-2 mb-2">
                <History size={14} className="text-[#5C7A4F]" />
                <h4 className="font-heading text-sm uppercase tracking-[0.2em] text-[#6F6656]">Payouts</h4>
              </div>
              {payouts.length === 0 && <p className="text-[#6F6656] text-sm">No cash paid out yet.</p>}
              {payouts.map((p) => (
                <div key={p.id} className="rounded-[20px] bg-white p-3 flex items-center justify-between mb-2" data-testid={`payout-${p.id}`}>
                  <div>
                    <div className="text-sm text-[#29241A]"><b>${p.amount.toFixed(2)}</b>{p.note && <span className="text-[#6F6656] text-xs"> · {p.note}</span>}</div>
                    <div className="text-[10px] text-[#6F6656]">{new Date(p.paid_at).toLocaleString()}</div>
                  </div>
                  <span className="text-emerald-700 text-xs font-medium">paid</span>
                </div>
              ))}
            </section>
            <section data-testid="kid-cashout-history">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={14} className="text-[#E0A33A]" />
                <h4 className="font-heading text-sm uppercase tracking-[0.2em] text-[#6F6656]">Cash-out requests</h4>
              </div>
              {cashouts.length === 0 && <p className="text-[#6F6656] text-sm">No requests yet.</p>}
              {cashouts.map((c) => (
                <div key={c.id} className="rounded-[20px] bg-white p-3 flex items-center justify-between mb-2" data-testid={`cashout-hist-${c.id}`}>
                  <div>
                    <div className="text-sm text-[#29241A]"><b>${c.amount.toFixed(2)}</b></div>
                    <div className="text-[10px] text-[#6F6656]">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    c.status === "approved" ? "text-emerald-700 bg-emerald-500/10"
                    : c.status === "denied" ? "text-[#E66B59] bg-[#E66B59]/10"
                    : "text-[#E0A33A] bg-[#E0A33A]/10"
                  }`}>{c.status}</span>
                </div>
              ))}
            </section>
            <section data-testid="kid-reduction-history">
              <div className="flex items-center gap-2 mb-2">
                <Bell size={14} className="text-[#E66B59]" />
                <h4 className="font-heading text-sm uppercase tracking-[0.2em] text-[#6F6656]">Time reductions</h4>
              </div>
              {reductions.length === 0 && <p className="text-[#6F6656] text-sm">No reductions.</p>}
              {reductions.map((r) => (
                <div key={r.id} className="rounded-[20px] bg-white p-3 mb-2" data-testid={`reduction-${r.id}`}>
                  <div className="text-sm text-[#29241A]">{r.message || `-${r.minutes} min${r.category ? " · " + r.category : ""}`}</div>
                  <div className="text-[10px] text-[#6F6656]">{new Date(r.applied_at).toLocaleString()}</div>
                </div>
              ))}
            </section>
          </div>
        )}
      </main>

      {cashoutOpen && wallet && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" data-testid="cashout-modal">
          <form onSubmit={submitCashout} className="rounded-[20px] bg-white p-8 w-full max-w-sm fade-in-up">
            <h3 className="font-heading text-2xl font-semibold text-[#29241A]">Ask for cash</h3>
            <p className="text-[#6F6656] text-sm mt-1">Balance available: <b className="text-[#5C7A4F]">${wallet.balance.toFixed(2)}</b></p>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6F6656]">Amount ($)</label>
                <input required type="number" step="0.25" min="0.25" max={wallet.balance} value={cashoutForm.amount}
                       onChange={(e) => setCashoutForm({ ...cashoutForm, amount: e.target.value })}
                       data-testid="cashout-amount-input"
                       className="mt-1 w-full rounded-xl border border-[#E8DFC9] bg-white px-4 py-3" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6F6656]">Note (optional)</label>
                <input value={cashoutForm.note} onChange={(e) => setCashoutForm({ ...cashoutForm, note: e.target.value })}
                       placeholder="What are you saving up for?"
                       data-testid="cashout-note-input"
                       className="mt-1 w-full rounded-xl border border-[#E8DFC9] bg-white px-4 py-3" />
              </div>
              {cashoutErr && <div className="text-sm text-[#E66B59] bg-[#E66B59]/10 rounded-xl px-3 py-2" data-testid="cashout-error">{cashoutErr}</div>}
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setCashoutOpen(false)} className="flex-1 rounded-xl border border-[#E8DFC9] bg-white px-4 py-3 font-medium" data-testid="cashout-cancel">Cancel</button>
              <button type="submit" className="flex-1 rounded-xl bg-[#E0A33A] text-white px-4 py-3 font-medium hover:bg-[#C98A22]" data-testid="cashout-submit">Ask</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
